import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import Loader from "../../components/Loader";
import { crmHeaders, crmMultipartHeaders } from "../../utils/crmApi";
import { canEditSmsTemplates } from "../../utils/userAccess";

const BASE_URL = process.env.REACT_APP_BASE_URL;

export default function SendSms() {
    const location = useLocation();
    const replyState = location.state || {};
    const [loading, setLoading] = useState(true);
    const [configured, setConfigured] = useState(false);
    const [fromNumber, setFromNumber] = useState("");
    const [countries, setCountries] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [mode, setMode] = useState("single");
    const [country, setCountry] = useState("");
    const [templateId, setTemplateId] = useState("");
    const [singlePhone, setSinglePhone] = useState(replyState.phone || "");
    const [singleName, setSingleName] = useState(replyState.name || "");
    const [singleMessage, setSingleMessage] = useState("");
    const [bulkFile, setBulkFile] = useState(null);
    const [sending, setSending] = useState(false);

    useEffect(() => {
        Promise.all([
            axios.get(`${BASE_URL}/sms/config`, { headers: crmHeaders() }),
            axios.get(`${BASE_URL}/getcountries`, { headers: crmHeaders() }),
            axios.get(`${BASE_URL}/sms/templates`, { headers: crmHeaders() }),
        ])
            .then(([configRes, countriesRes, templatesRes]) => {
                setConfigured(!!configRes.data.configured);
                setFromNumber(configRes.data.fromNumber || "");
                setCountries(countriesRes.data.filter((c) => c.Status === "Active"));
                setTemplates(templatesRes.data);
            })
            .catch(console.error)
            .finally(() => setLoading(false));
    }, []);

    const countryTemplates = templates.filter((t) => !country || t.Country === country);

    useEffect(() => {
        if (countryTemplates.length && !templateId) {
            setTemplateId(String(countryTemplates[0].Id));
        }
    }, [country, countryTemplates, templateId]);

    const selectedTemplate = templates.find((t) => String(t.Id) === String(templateId));

    const handleSingleSend = (e) => {
        e.preventDefault();
        if (!singlePhone.trim() || !singleMessage.trim()) {
            toast.error("Phone and message are required");
            return;
        }
        setSending(true);
        axios
            .post(
                `${BASE_URL}/sms/send`,
                {
                    phoneNumber: singlePhone,
                    message: singleMessage,
                    country: country || null,
                    clientName: singleName || null,
                },
                { headers: crmHeaders() }
            )
            .then(() => {
                toast.success("SMS sent successfully");
                setSinglePhone("");
                setSingleName("");
                setSingleMessage("");
            })
            .catch((err) => toast.error(err.response?.data?.message || "Send failed"))
            .finally(() => setSending(false));
    };

    const handleBulkSend = (e) => {
        e.preventDefault();
        if (!bulkFile) {
            toast.error("Please upload a CSV file");
            return;
        }
        if (!templateId && !singleMessage.trim()) {
            toast.error("Select a template or enter a custom message");
            return;
        }

        const formData = new FormData();
        formData.append("file", bulkFile);
        if (country) formData.append("country", country);
        if (templateId) formData.append("templateId", templateId);
        if (singleMessage.trim()) formData.append("message", singleMessage.trim());

        setSending(true);
        axios
            .post(`${BASE_URL}/sms/send-bulk`, formData, { headers: crmMultipartHeaders() })
            .then((res) => {
                toast.success(res.data.message || "Bulk send complete");
                if (res.data.errors?.length) {
                    console.warn("Bulk SMS errors:", res.data.errors);
                }
            })
            .catch((err) => toast.error(err.response?.data?.message || "Bulk send failed"))
            .finally(() => setSending(false));
    };

    const applyTemplatePreview = () => {
        if (!selectedTemplate) return;
        setSingleMessage(
            selectedTemplate.Template.replace(/\{clientName\}/gi, "John")
                .replace(/\{businessName\}/gi, "Acme Corp")
                .replace(/\{phoneNumber\}/gi, "+1234567890")
                .replace(/\{email\}/gi, "john@example.com")
        );
    };

    if (loading) return <Loader />;

    return (
        <div className="container">
            <ToastContainer />
            <div className="crm-page-header">
                <div>
                    <h1 className="crm-page-title">Send SMS</h1>
                    <p className="crm-page-subtitle">
                        Twilio {configured ? `from ${fromNumber}` : "not configured — add credentials in server/.env"}
                    </p>
                </div>
                {canEditSmsTemplates() && (
                    <Link to="/sms/templates" className="btn btn-outline-primary">
                        Manage Templates
                    </Link>
                )}
            </div>

            {!configured && (
                <div className="alert alert-warning">
                    Set <code>TWILIO_ACCOUNT_SID</code>, <code>TWILIO_AUTH_TOKEN</code>, and{" "}
                    <code>TWILIO_PHONE_NUMBER</code> in server/.env
                </div>
            )}

            <ul className="nav nav-tabs mb-4">
                <li className="nav-item">
                    <button
                        type="button"
                        className={`nav-link ${mode === "single" ? "active" : ""}`}
                        onClick={() => setMode("single")}
                    >
                        Single SMS
                    </button>
                </li>
                <li className="nav-item">
                    <button
                        type="button"
                        className={`nav-link ${mode === "bulk" ? "active" : ""}`}
                        onClick={() => setMode("bulk")}
                    >
                        Bulk SMS (CSV)
                    </button>
                </li>
            </ul>

            <div className="row g-3 mb-4">
                <div className="col-md-4">
                    <label className="form-label">Country (optional)</label>
                    <select
                        className="form-select"
                        value={country}
                        onChange={(e) => {
                            setCountry(e.target.value);
                            setTemplateId("");
                        }}
                    >
                        <option value="">All / none</option>
                        {countries.map((c) => (
                            <option key={c.Id} value={c.Country}>
                                {c.Country}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="col-md-4">
                    <label className="form-label">SMS Template</label>
                    <select
                        className="form-select"
                        value={templateId}
                        onChange={(e) => setTemplateId(e.target.value)}
                    >
                        <option value="">Custom message</option>
                        {countryTemplates.map((t) => (
                            <option key={t.Id} value={t.Id}>
                                {t.Country} — {t.Template.slice(0, 50)}...
                            </option>
                        ))}
                    </select>
                </div>
                {selectedTemplate && (
                    <div className="col-md-4 d-flex align-items-end">
                        <button type="button" className="btn btn-outline-secondary" onClick={applyTemplatePreview}>
                            Load template into message
                        </button>
                    </div>
                )}
            </div>

            {mode === "single" ? (
                <form onSubmit={handleSingleSend} className="crm-user-form-card p-4">
                    <div className="row g-3">
                        <div className="col-md-6">
                            <label className="form-label">Phone Number</label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="+1234567890"
                                value={singlePhone}
                                onChange={(e) => setSinglePhone(e.target.value)}
                                required
                            />
                        </div>
                        <div className="col-md-6">
                            <label className="form-label">Client Name (optional)</label>
                            <input
                                type="text"
                                className="form-control"
                                value={singleName}
                                onChange={(e) => setSingleName(e.target.value)}
                            />
                        </div>
                        <div className="col-12">
                            <label className="form-label">Message</label>
                            <textarea
                                className="form-control"
                                rows={5}
                                maxLength={1600}
                                value={singleMessage}
                                onChange={(e) => setSingleMessage(e.target.value)}
                                required
                            />
                            <small className="text-muted">{singleMessage.length}/1600 characters</small>
                        </div>
                    </div>
                    <button
                        type="submit"
                        className="btn btn-primary mt-3"
                        disabled={sending || !configured}
                    >
                        {sending ? "Sending..." : "Send SMS"}
                    </button>
                </form>
            ) : (
                <form onSubmit={handleBulkSend} className="crm-user-form-card p-4">
                    <p className="text-muted">
                        CSV columns: <strong>phoneNumber | clientName | businessName | email</strong> (max 2000
                        rows). Template placeholders are filled per row.
                    </p>
                    <div className="mb-3">
                        <label className="form-label">Upload CSV</label>
                        <input
                            type="file"
                            className="form-control"
                            accept=".csv"
                            onChange={(e) => setBulkFile(e.target.files?.[0] || null)}
                            required
                        />
                    </div>
                    {!templateId && (
                        <div className="mb-3">
                            <label className="form-label">Custom bulk message (if no template)</label>
                            <textarea
                                className="form-control"
                                rows={4}
                                value={singleMessage}
                                onChange={(e) => setSingleMessage(e.target.value)}
                                placeholder="Use {clientName}, {businessName}, etc."
                            />
                        </div>
                    )}
                    <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={sending || !configured}
                    >
                        {sending ? "Sending bulk..." : "Send Bulk SMS"}
                    </button>
                </form>
            )}
        </div>
    );
}
